/**
 * W-8BEN-E Service
 * Following the same clean architecture pattern as W8BEN, W8ECI, W8EXP, and W9 services
 */

import { W8BENEFormModel, W8BENEResponseModel, W8BENEFieldMapper } from "../Models/W8BENEModels";
import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { updateFileData } from "./fileDataService";

export class W8BENEService {
  private static readonly API_ENDPOINT = `${BASE_URL}/FileData/ImportFiles`;

  static fromApiResponse(apiData: any): W8BENEFormModel {
    // Create response model from API data
    const responseModel = new W8BENEResponseModel(apiData);

    const formModel = W8BENEFieldMapper.mapResponseToForm(responseModel);

    return formModel;
  }

  /**
   * Handle checkbox defaults - if a checkbox field isn't in API data, it should be false
   * Protection: Skip if isMapDone is true to preserve user selections
   */
  private static handleCheckboxDefaults(formModel: W8BENEFormModel, apiData: any): void {
    // 🛡️ PROTECTION: Skip checkbox defaults if isMapDone is true
    if (apiData && apiData.isMapDone === true) {
      // console.log("🛡️ W8BENEService: Skipping checkbox defaults - isMapDone is true");
      return;
    }

    const checkboxFields = [
      // Part I: Chapter 3 Status checkboxes
      "W8-BENE-4_corporation",
      "W8-BENE-4_partnership",
      "W8-BENE-4_simple_trust",
      "W8-BENE-4_tax_exempt_organization",
      "W8-BENE-4_complex_trust",
      "W8-BENE-4_foreign_government_controlled_entity",
      "W8-BENE-4_central_bank",
      "W8-BENE-4_private_foundation",
      "W8-BENE-4_estate",
      "W8-BENE-4_foreign_government_integral_part",
      "W8-BENE-4_grantor_trust",
      "W8-BENE-4_disregarded_entity",
      "W8-BENE-4_international_organization",

      // Part I: Chapter 4 Status (FATCA Status) checkboxes
      "W8-BENE-5_nonparticipating_ffi",
      "W8-BENE-5_participating_ffi",
      "W8-BENE-5_reporting_model1",
      "W8-BENE-5_reporting_model2",
      "W8-BENE-5_registered_deemed",
      "W8-BENE-5_sponsored_ffi",
      "W8-BENE-5_certified_local_bank",
      "W8-BENE-5_certified_low_value",
      "W8-BENE-5_certified_sponsored",
      "W8-BENE-5_certified_limited_life",
      "W8-BENE-5_investment_entities",
      "W8-BENE-5_owner_documented",
      "W8-BENE-5_restricted_distributor",
      "W8-BENE-5_nonreporting_iga",
      "W8-BENE-5_foreign_government",
      "W8-BENE-5_international_organization",
      "W8-BENE-5_exempt_retirement",
      "W8-BENE-5_entity_exempt_owners",
      "W8-BENE-5_territory_financial",
      "W8-BENE-5_excepted_group",
      "W8-BENE-5_excepted_startup",
      "W8-BENE-5_excepted_liquidation",
      "W8-BENE-5_fatca_501c_org_",
      "W8-BENE-5_nonprofit_org",
      "W8-BENE-5_publicly_traded",
      "W8-BENE-5_excepted_territory",
      "W8-BENE-5_active_nffe",
      "W8-BENE-5_passive_nffe",
      "W8-BENE-5_excepted_affiliate",
      "W8-BENE-5_direct_reporting",
      "W8-BENE-5_sponsored_direct",
      "W8-BENE-5_account_not_financial",

      // Other checkboxes
      "W8-BENE-FTIN_not_legally_required",

      // Part II: Disregarded Entity or Branch checkboxes
      "W8-BENE-11_branch_treated_as_nonparticipating_FFI",
      "W8-BENE-11_participating_FFI",
      "W8-BENE-11_reporting_Model1_FFI",
      "W8-BENE-11_reporting_Model2_FFI",
      "W8-BENE-11_us_branch",

      // Part III: Claim of Treaty Benefits checkboxes
      "W8-BENE-14A_beneficial_owner_resident",
      "W8-BENE-14B_beneficial_owner_derives_item",
      "W8-BENE-14B_government",
      "W8-BENE-14B_tax_exempt_pension_trust",
      "W8-BENE-14B_other_tax_exempt_organization",
      "W8-BENE-14B_publicly_traded_corporation",
      "W8-BENE-14B_subsidiary_of_publicly_traded",
      "W8-BENE-14B_company_meets_ownership_and_base_erosion_test",
      "W8-BENE-14B_company_meets_derivative",
      "W8-BENE-14B_company_meets_active_trade_or_business",
      "W8-BENE-14B_favorable_discretionary_determination",
      "W8-BENE-14B_no_lob",
      "W8-BENE-14B_other",
      "W8-BENE-14C_claiming_treaty_benefits",

      // Part IV: FATCA reporting checkboxes
      "W8-BENE-17_investment_entity",
      "W8-BENE-17_controlled_foreign_corporation",
      "W8-BENE-18_licensed_solely",
      "W8-BENE-19_investing_reinvesting_or_trading",
      "W8-BENE-21_qi_wp_wt",
      "W8-BENE-22_january_17_2013",
      "W8-BENE-23_section_1_1471",
      "W8-BENE-24A_owner_documented_FFI",
      "W8-BENE-24B_FFI_owner_reporting",
      "W8-BENE-24C_auditor_letter",
      "W8-BENE-24D_contingent_beneficiaries",
      "W8-BENE-25A_restricted_distributors",
      "W8-BENE-25B_restricted_distributor_bound_agreement",
      "W8-BENE-25C_restricted_distributor_current_agreement",
      "W8-BENE-26_nonreporting_iga_country",
      "W8-BENE-26_model1_iga",
      "W8-BENE-26_model2_iga",
      "W8-BENE-26_trustee_us",
      "W8-BENE-26_trustee_foreign",
      "W8-BENE-27_foreign_government_checkbox",
      "W8-BENE-28A_international_org_7701_checkbox",
      "W8-BENE-28B_international_org_intergovernmental",
      "W8-BENE-29A_retirement_plan_treaty",
      "W8-BENE-29B_retirement_plan_organized",
      "W8-BENE-29C_retirement_plan_fewer_50",
      "W8-BENE-29D_retirement_plan_401a",
      "W8-BENE-29E_retirement_plan_earn_income",
      "W8-BENE-29F_retirement_plan_government_sponsored",
      "W8-BENE-30_entity_wholly_owned",
      "W8-BENE-31_territory_financial_institution",
      "W8-BENE-32_excepted_nonfinancial_group",
      "W8-BENE-34_excepted_liquidation",
      "W8-BENE_35_section_501c",
      "W8-BENE-37A_publicly_traded_nffe",
      "W8-BENE-38_excepted_territory_nffe",
      "W8-BENE-39_active_nffe",
      "W8-BENE-40A_traded_nffe",
      "W8-BENE-40B_passive_nffe_no_substantial_owners",
      "W8-BENE-40C_passive_nffe_substantial_owners_provided",
      "W8-BENE-41_affiliated_group",
      "W8-BENE-43_nffe",

      // Signing capacity
      "Signing_capacity",

      // Other legacy checkboxes
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    checkboxFields.forEach((field) => {
      // If the field is not present in API data, set it to false
      if (!(field in apiData)) {
        (formModel as any)[field] = false;
      } else {
        // If it is present, convert to boolean properly
        const value = apiData[field];
        const boolValue = this.convertToBoolean(value);
        (formModel as any)[field] = boolValue;
      }
    });
  }

  /**
   * Convert various value types to boolean for checkboxes
   */
  private static convertToBoolean(value: any): boolean {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      return (
        value.toLowerCase() === "true" ||
        value === "1" ||
        value.toLowerCase() === "yes" ||
        value.toLowerCase() === "selected"
      );
    }
    if (typeof value === "number") return value === 1;
    return !!value;
  }

  /**
   * Map legacy API field names to current field names
   * Protection: Skip if isMapDone is true to preserve user interactions
   */
  private static mapLegacyApiFields(apiData: any, responseModel: W8BENEResponseModel): void {
    // 🛡️ PROTECTION: Skip legacy field mapping if isMapDone is true
    if (apiData && apiData.isMapDone === true) {
      // console.log("🛡️ W8BENEService: Skipping legacy field mapping - isMapDone is true");
      return;
    }
    const legacyMappings: Record<string, string[]> = {
      "Name of organization": [
        "Organization_name",
        "organization_name",
        "Name",
        "name",
        "entity_name",
      ],
      "Country of incorporation or organization": [
        "Country_of_incorporation",
        "incorporation_country",
        "Country_of_incorporation_organization",
        "W8-BENE-Country_of_incorporation_organization",
      ],
      "Permanent residence address": [
        "Permanent_residence_address",
        "permanent_address",
        "address_line_1",
        "Permanent residence address",
        "W8-BENE-Permanent residence address",
      ],
      "Permanent city address": [
        "Permanent_city_address",
        "permanent_city",
        "city_state_zip",
        "Permanent city address",
        "W8-BENE-Permanent_address_city_state_postal",
      ],
      "Permanent address country": [
        "Permanent_address_country",
        "permanent_country",
        "country",
        "Permanent address country",
        "W8-BENE-Permanent_address_country",
      ],
      "Mailing address": [
        "Mailing_address",
        "mailing_address",
        "Mailing address",
        "W8-BENE-Mailing_address_street",
      ],
      "W8-BENE-Mailing_address_city_state_postal": [
        "W8-BENE-Mailing_address_city_state_postal",
        "Mailing_address_city_state_postal",
      ],
      "Mailing address country": [
        "Mailing_address_country",
        "mailing_country",
        "Mailing address country",
        "W8-BENE-Mailing_address_country",
      ],
      "U.S. taxpayer identification number": [
        "US_taxpayer_identification_number",
        "us_tin",
        "U.S._taxpayer_identification_number",
        "W8-BENE-8_us_taxpayer_id",
      ],
      GIIN: ["GIIN", "giin", "global_intermediary_identification_number", "W8-BENE-9A_giin"],
      "Foreign tax identifying number": [
        "Foreign_tax_identifying_number",
        "foreign_tin",
        "Foreign_tax_identifying_number",
        "W8-BENE-Foreign_tax_identifying_number",
      ],
      "W8-BENE-10_reference_number": [
        "W8-BENE-10_reference_number",
        "reference_number",
        "Reference_number",
      ],
      GIINCheckBox: ["GIINCheckBox", "giin_checkbox", "giin_check"],
      "GIIN Applied For": ["GIIN Applied For", "giin_applied_for", "giin_applied"],
      "Foreign TIN": ["Foreign TIN", "foreign_tin_checkbox", "foreign_tin_check"],
      "W8-BENE-FTIN_not_legally_required": [
        "W8-BENE-FTIN_not_legally_required",
        "ftin_not_required",
      ],

      // Disregarded entity name and address fields
      // NOTE: Extraction API currently does not provide disregarded entity name field
      // This field must be entered manually until extraction engine is updated
      "Name of disregarded entity": [
        "W8-BENE-disregarded_entity_name", // Expected field name (not currently extracted)
        "disregarded_entity_name",
        "Name of disregarded entity",
        "W8-BENE-Name_of_disregarded_entity",
      ],
      "W8-BENE-12_disregarded_entity_address": [
        "W8-BENE-12_disregarded_entity_address",
        "disregarded_entity_address",
      ],
      "W8-BENE-12_disregarded_entity_city_state_postal": [
        "W8-BENE-12_disregarded_entity_city_state_postal",
        "disregarded_entity_city",
      ],
      "W8-BENE-12_disregarded_entity_country": [
        "W8-BENE-12_disregarded_entity_country",
        "disregarded_entity_country",
      ],
      "W8-BENE-13_giin": ["W8-BENE-13_giin", "disregarded_entity_giin"],

      // Treaty benefit fields
      "W8-BENE-14A_country_name": ["W8-BENE-14A_country_name", "treaty_country"],
      "W8-BENE-15_treaty_provision_and_paragraph": [
        "W8-BENE-15_treaty_provision_and_paragraph",
        "treaty_provision",
      ],
      "W8-BENE-15_withholding_rate_percentage": [
        "W8-BENE-15_withholding_rate_percentage",
        "withholding_rate",
      ],
      "W8-BENE-15_income_type": ["W8-BENE-15_income_type", "income_type"],
      "W8-BENE-15_additional_conditions_explaination": [
        "W8-BENE-15_additional_conditions_explaination",
        "additional_conditions",
      ],

      // Sponsoring entity names
      "W8-BENE-16_name_of_sponsoring_entity": [
        "W8-BENE-16_name_of_sponsoring_entity",
        "sponsoring_entity_name",
      ],
      "W8-BENE-20_sponsoring_entity_name": [
        "W8-BENE-20_sponsoring_entity_name",
        "sponsoring_entity_name_2",
      ],
      "W8-BENE-26_trustee_sponsor_name": [
        "W8-BENE-26_trustee_sponsor_name",
        "trustee_sponsor_name",
      ],

      // Date fields
      "W8-BENE-35_section_501c_determination_date": [
        "W8-BENE-35_section_501c_determination_date",
        "determination_date",
      ],
      "W8-BENE-37A_securities_exchange_name": [
        "W8-BENE-37A_securities_exchange_name",
        "securities_exchange",
      ],

      // Missing checkboxes from 27 to 42
      "W8-BENE-27_foreign_government_checkbox": [
        "W8-BENE-27_foreign_government_checkbox",
        "foreign_government_checkbox",
      ],
      "W8-BENE-28A_international_org_7701_checkbox": [
        "W8-BENE-28A_international_org_7701_checkbox",
        "international_org_7701",
      ],
      "W8-BENE-28B_international_org_intergovernmental": [
        "W8-BENE-28B_international_org_intergovernmental",
        "international_org_intergovernmental",
      ],
      "W8-BENE-29A_retirement_plan_treaty": [
        "W8-BENE-29A_retirement_plan_treaty",
        "retirement_plan_treaty",
      ],
      "W8-BENE-29B_retirement_plan_organized": [
        "W8-BENE-29B_retirement_plan_organized",
        "retirement_plan_organized",
      ],
      "W8-BENE-29C_retirement_plan_fewer_50": [
        "W8-BENE-29C_retirement_plan_fewer_50",
        "retirement_plan_fewer_50",
      ],
      "W8-BENE-29D_retirement_plan_401a": [
        "W8-BENE-29D_retirement_plan_401a",
        "retirement_plan_401a",
      ],
      "W8-BENE-29E_retirement_plan_earn_income": [
        "W8-BENE-29E_retirement_plan_earn_income",
        "retirement_plan_earn_income",
      ],
      "W8-BENE-29F_retirement_plan_government_sponsored": [
        "W8-BENE-29F_retirement_plan_government_sponsored",
        "retirement_plan_government_sponsored",
      ],
      "W8-BENE-30_entity_wholly_owned": ["W8-BENE-30_entity_wholly_owned", "entity_wholly_owned"],
      "W8-BENE-31_territory_financial_institution": [
        "W8-BENE-31_territory_financial_institution",
        "territory_financial_institution",
      ],
      "W8-BENE-32_excepted_nonfinancial_group": [
        "W8-BENE-32_excepted_nonfinancial_group",
        "excepted_nonfinancial_group",
      ],
      "W8-BENE-34_excepted_liquidation": [
        "W8-BENE-34_excepted_liquidation",
        "excepted_liquidation",
      ],
      "W8-BENE_35_section_501c": ["W8-BENE_35_section_501c", "section_501c"],
      "W8-BENE-37A_publicly_traded_nffe": [
        "W8-BENE-37A_publicly_traded_nffe",
        "publicly_traded_nffe",
      ],
      "W8-BENE-37B_affiliated_nffe": ["W8-BENE-37B_nffe_affiliate"],
      "W8-BENE-38_excepted_territory_nffe": [
        "W8-BENE-38_excepted_territory_nffe",
        "excepted_territory_nffe",
      ],
      "W8-BENE-39_active_nffe": ["W8-BENE-39_active_nffe", "active_nffe"],
      "W8-BENE-40A_traded_nffe": ["W8-BENE-40A_traded_nffe", "traded_nffe"],
      "W8-BENE-40B_passive_nffe_no_substantial_owners": [
        "W8-BENE-40B_passive_nffe_no_substantial_owners",
        "passive_nffe_no_substantial_owners",
      ],
      "W8-BENE-40C_passive_nffe_substantial_owners_provided": [
        "W8-BENE-40C_passive_nffe_substantial_owners_provided",
        "passive_nffe_substantial_owners_provided",
      ],
      "W8-BENE-41_affiliated_group": ["W8-BENE-41_affiliated_group", "affiliated_group"],
      "W8-BENE-42_sponsoring_entity": ["W8-BENE-42_sponsoring_entity", "sponsoring_entity_name_3"],
      "W8-BENE-43_nffe": ["W8-BENE-43_nffe", "nffe"],

      // Chapter 4 Status fields
      "W8-BENE-11_branch_treated_as_nonparticipating_FFI": [
        "W8-BENE-11_branch_treated_as_nonparticipating_FFI",
        "branch_nonparticipating",
      ],
      "W8-BENE-11_participating_FFI": ["W8-BENE-11_participating_FFI", "participating_ffi"],
      "W8-BENE-11_reporting_Model1_FFI": ["W8-BENE-11_reporting_Model1_FFI", "reporting_model1"],
      "W8-BENE-11_reporting_Model2_FFI": ["W8-BENE-11_reporting_Model2_FFI", "reporting_model2"],
      "W8-BENE-11_us_branch": ["W8-BENE-11_us_branch", "us_branch"],

      // Treaty benefit fields
      "14a": ["14a", "treaty_resident"],
      "14b": ["14b", "qualified_resident"],
      "14c": ["14c", "derivative_benefits"],
      "W8-BENE-14B_government": ["W8-BENE-14B_government", "government"],
      "W8-BENE-14B_tax_exempt_pension_trust": [
        "W8-BENE-14B_tax_exempt_pension_trust",
        "pension_trust",
      ],
      "W8-BENE-14B_other_tax_exempt_organization": [
        "W8-BENE-14B_other_tax_exempt_organization",
        "tax_exempt_org",
      ],
      "W8-BENE-14B_publicly_traded_corporation": [
        "W8-BENE-14B_publicly_traded_corporation",
        "publicly_traded",
      ],
      "W8-BENE-14B_subsidiary_of_publicly_traded": [
        "W8-BENE-14B_subsidiary_of_publicly_traded",
        "subsidiary_traded",
      ],

      // FATCA Status fields
      "W8-BENE-17_controlled_foreign_corporation": [
        "W8-BENE-17_controlled_foreign_corporation",
        "cfc",
      ],
      "W8-BENE-17_investment_entity": ["W8-BENE-17_investment_entity", "investment_entity"],
      "W8-BENE-18_licensed_solely": ["W8-BENE-18_licensed_solely", "licensed_solely"],
      "W8-BENE-19_investing_reinvesting_or_trading": [
        "W8-BENE-19_investing_reinvesting_or_trading",
        "investing_trading",
      ],
      "W8-BENE-21_qi_wp_wt": ["W8-BENE-21_qi_wp_wt", "qi_wp_wt"],
      "W8-BENE-22_january_17_2013": ["W8-BENE-22_january_17_2013", "january_2013"],
      "W8-BENE-23_section_1_1471": ["W8-BENE-23_section_1_1471", "section_1471"],

      // Part XII: Treated as field
      "W8-BENE-26_treated_as": ["W8-BENE-26_treated_as", "treated_as", "W8-BENE-26_treated_as"],

      // Signature fields
      Signature_date: ["Signature_date", "signature_date", "sign_date"],
      signature_date: ["signature_date", "Signature_date", "sign_date"],
      Print_name: ["Print_name", "printed_name", "signer_name", "Signer_printed_name"],
      Signing_capacity: ["Signing_capacity", "signing_capacity", "capacity"],
      certify: ["certify", "certification", "is_certified"],
      isSigned: ["isSigned", "is_signed", "signed"],
      isModified: ["isModified", "is_modified", "modified"],
      Form_name: ["Form_name", "form_name", "form_type"],
      Form_revision: ["Form_revision", "form_revision", "revision_date"],
    };

    // Apply legacy mappings
    Object.entries(legacyMappings).forEach(([currentField, legacyFields]) => {
      for (const legacyField of legacyFields) {
        if (apiData[legacyField] !== undefined) {
          responseModel[currentField] = apiData[legacyField];
          break;
        }
      }
    });

    // Convert string boolean values to actual booleans for checkbox fields
    const checkboxFields = [
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
      "W8-BENE-FTIN_not_legally_required",
      "14a",
      "14b",
      "14c",
      "certify",
      "W8-BENE-11_branch_treated_as_nonparticipating_FFI",
      "W8-BENE-11_participating_FFI",
      "W8-BENE-11_reporting_Model1_FFI",
      "W8-BENE-11_reporting_Model2_FFI",
      "W8-BENE-11_us_branch",
      "W8-BENE-14B_government",
      "W8-BENE-14B_tax_exempt_pension_trust",
      "W8-BENE-14B_other_tax_exempt_organization",
      "W8-BENE-14B_publicly_traded_corporation",
      "W8-BENE-14B_subsidiary_of_publicly_traded",
      "W8-BENE-17_controlled_foreign_corporation",
      "W8-BENE-17_investment_entity",
      "W8-BENE-18_licensed_solely",
      "W8-BENE-19_investing_reinvesting_or_trading",
      "W8-BENE-21_qi_wp_wt",
      "W8-BENE-22_january_17_2013",
      "W8-BENE-23_section_1_1471",
    ];

    checkboxFields.forEach((field) => {
      if (responseModel[field] !== undefined) {
        const value = responseModel[field];
        if (typeof value === "string") {
          responseModel[field] =
            value.toLowerCase() === "true" ||
            value === "1" ||
            value.toLowerCase() === "yes" ||
            value.toLowerCase() === "selected";
        }
      }
    });
  }

  /**
   * Map dynamic treaty fields from API response
   * Protection: Skip if isMapDone is true to preserve user interactions
   */
  private static mapTreatyFields(apiData: any, responseModel: W8BENEResponseModel): void {
    // 🛡️ PROTECTION: Skip treaty field mapping if isMapDone is true
    if (apiData && apiData.isMapDone === true) {
      // console.log("🛡️ W8BENEService: Skipping treaty field mapping - isMapDone is true");
      return;
    }

    // Handle indexed treaty fields (BENE-15-Treaty_article_0, BENE-15-Treaty_percentage_0, etc.)
    Object.keys(apiData).forEach((apiField) => {
      if (
        apiField.includes("BENE-15-Treaty_article_") ||
        apiField.includes("BENE-15-Treaty_percentage_") ||
        apiField.includes("BENE-15-Treaty_typeOfIncome_") ||
        apiField.includes("BENE-15-Treaty_paragraph_") ||
        apiField.includes("BENE-15-Treaty_subparagraph_")
      ) {
        (responseModel as any)[apiField] = apiData[apiField];
      }
    });

    // Handle treatyClaims JSON string
    if (apiData.treatyClaims && typeof apiData.treatyClaims === "string") {
      try {
        // const claims = JSON.parse(apiData.treatyClaims);

        (responseModel as any)["treatyClaims"] = apiData.treatyClaims;
      } catch (error) {
        console.warn("Failed to parse treatyClaims:", error);
      }
    }
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: W8BENEFormModel,
    row: any,
    selectedIndex: number = 0
  ): Promise<boolean> {
    try {
      // Enhanced authentication with multiple fallbacks
      let userData;
      let token;

      try {
        userData = getUserIdFromLocalStorage();
        token = userData?.token;
      } catch (error) {
        console.log("W8BEN-E Auth - getUserIdFromLocalStorage failed:", error);
      }

      // Fallback authentication methods
      if (!token) {
        try {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
            const parsed = JSON.parse(storedUserData);
            token = parsed.token;
          }
        } catch (error) {
          console.log("W8BEN-E Auth - fallback userData parsing failed:", error);
        }
      }

      if (!token) {
        try {
          token = localStorage.getItem("token");
        } catch (error) {
          console.log("W8BEN-E Auth - direct token retrieval failed:", error);
        }
      }

      if (!token) {
        console.error("W8BEN-E Auth - No authentication token found after all fallbacks");
        throw new Error("No authentication token found");
      }

      // Convert model to API payload
      const formData = this.toApiPayload(model);

      delete formData.giinValidationMessage;
      delete formData.tinValidationMessage;

      let parsedFormsData;
      try {
        // Parse formsData and check if it's complex structure or simple array
        parsedFormsData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedFormsData &&
          typeof parsedFormsData === "object" &&
          parsedFormsData.formsData &&
          Array.isArray(parsedFormsData.formsData)
        ) {
          // Find and update the W8BEN-E form in the complex structure
          let formUpdated = false;

          for (let i = 0; i < parsedFormsData.formsData.length; i++) {
            const formItem = parsedFormsData.formsData[i];
            if (formItem && formItem.data && Array.isArray(formItem.data)) {
              for (let j = 0; j < formItem.data.length; j++) {
                const dataItem = formItem.data[j];
                if (
                  dataItem &&
                  (dataItem.formType === "W8BENE" || dataItem.Form_number === "W-8BEN-E")
                ) {
                  parsedFormsData.formsData[i].data[j] = {
                    ...dataItem,
                    ...formData,
                  };
                  formUpdated = true;
                  break;
                }
              }
              if (formUpdated) break;
            }
          }

          if (!formUpdated) {
            parsedFormsData.formsData.push({
              request_id: "new-w8bene-" + Date.now(),
              status: "success",
              data: [formData],
              reportdata: {
                requestId: "new-w8bene-" + Date.now(),
                subRequestId: "new-w8bene-" + Date.now(),
                taxForms: "W8BENE",
                type: "extraction",
              },
            });
          }
        } else {
          const formsDataArray = Array.isArray(parsedFormsData) ? parsedFormsData : [];

          // Update the form at the specified index (or add if it doesn't exist)
          const currentItem = formsDataArray[selectedIndex];

          if (selectedIndex < formsDataArray.length && currentItem) {
            // Update existing form data by merging with new data
            formsDataArray[selectedIndex] = {
              ...currentItem,
              ...formData,
            };
          } else {
            // Ensure array is large enough
            while (formsDataArray.length <= selectedIndex) {
              formsDataArray.push({});
            }
            // Add new form data
            formsDataArray[selectedIndex] = formData;
          }

          parsedFormsData = formsDataArray;
        }
      } catch (error) {
        parsedFormsData = [formData];
      }

      // Store back as stringified structure (preserving complex format if applicable)
      row.formsData = JSON.stringify(parsedFormsData);

      // Send to API using centralized service
      await updateFileData(row);

      return true;
    } catch (error) {
      console.error("Failed to save W-8BEN-E form:", error);
      throw error;
    }
  }

  /**
   * Convert form model to API payload format
   * 🔧 CRITICAL FIX: Send ALL checkbox fields (both true AND false values) to ensure unchecked boxes are saved properly
   */
  static toApiPayload(model: W8BENEFormModel): Record<string, any> {
    const payload: Record<string, any> = {};

    const checkboxFields = [
      // Part I: Chapter 3 Status checkboxes
      "W8-BENE-4_corporation",
      "W8-BENE-4_partnership",
      "W8-BENE-4_simple_trust",
      "W8-BENE-4_tax_exempt_organization",
      "W8-BENE-4_complex_trust",
      "W8-BENE-4_foreign_government_controlled_entity",
      "W8-BENE-4_central_bank",
      "W8-BENE-4_private_foundation",
      "W8-BENE-4_estate",
      "W8-BENE-4_foreign_government_integral_part",
      "W8-BENE-4_grantor_trust",
      "W8-BENE-4_disregarded_entity",
      "W8-BENE-4_international_organization",

      // Part I: Chapter 4 Status (FATCA Status) checkboxes
      "W8-BENE-5_nonparticipating_ffi",
      "W8-BENE-5_participating_ffi",
      "W8-BENE-5_reporting_model1",
      "W8-BENE-5_reporting_model2",
      "W8-BENE-5_registered_deemed",
      "W8-BENE-5_sponsored_ffi",
      "W8-BENE-5_certified_local_bank",
      "W8-BENE-5_certified_low_value",
      "W8-BENE-5_certified_sponsored",
      "W8-BENE-5_certified_limited_life",
      "W8-BENE-5_investment_entities",
      "W8-BENE-5_owner_documented",
      "W8-BENE-5_restricted_distributor",
      "W8-BENE-5_nonreporting_iga",
      "W8-BENE-5_foreign_government",
      "W8-BENE-5_international_organization",
      "W8-BENE-5_exempt_retirement",
      "W8-BENE-5_entity_exempt_owners",
      "W8-BENE-5_territory_financial",
      "W8-BENE-5_excepted_group",
      "W8-BENE-5_excepted_startup",
      "W8-BENE-5_excepted_liquidation",
      "W8-BENE-5_fatca_501c_org_",
      "W8-BENE-5_nonprofit_org",
      "W8-BENE-5_publicly_traded",
      "W8-BENE-5_excepted_territory",
      "W8-BENE-5_active_nffe",
      "W8-BENE-5_passive_nffe",
      "W8-BENE-5_excepted_affiliate",
      "W8-BENE-5_direct_reporting",
      "W8-BENE-5_sponsored_direct",
      "W8-BENE-5_account_not_financial",

      // Other checkboxes
      "W8-BENE-FTIN_not_legally_required",

      // Part II: Disregarded Entity or Branch checkboxes
      "W8-BENE-11_branch_treated_as_nonparticipating_FFI",
      "W8-BENE-11_participating_FFI",
      "W8-BENE-11_reporting_Model1_FFI",
      "W8-BENE-11_reporting_Model2_FFI",
      "W8-BENE-11_us_branch",

      // Part III: Claim of Treaty Benefits checkboxes
      "W8-BENE-14A_beneficial_owner_resident",
      "W8-BENE-14B_beneficial_owner_derives_item",
      "W8-BENE-14B_government",
      "W8-BENE-14B_tax_exempt_pension_trust",
      "W8-BENE-14B_other_tax_exempt_organization",
      "W8-BENE-14B_publicly_traded_corporation",
      "W8-BENE-14B_subsidiary_of_publicly_traded",
      "W8-BENE-14B_company_meets_ownership_and_base_erosion_test",
      "W8-BENE-14B_company_meets_derivative",
      "W8-BENE-14B_company_meets_active_trade_or_business",
      "W8-BENE-14B_favorable_discretionary_determination",
      "W8-BENE-14B_no_lob",
      "W8-BENE-14B_other",
      "W8-BENE-14C_claiming_treaty_benefits",

      // Part IV: FATCA reporting checkboxes
      "W8-BENE-17_investment_entity",
      "W8-BENE-17_controlled_foreign_corporation",
      "W8-BENE-18_licensed_solely",
      "W8-BENE-19_investing_reinvesting_or_trading",
      "W8-BENE-21_qi_wp_wt",
      "W8-BENE-22_january_17_2013",
      "W8-BENE-23_section_1_1471",
      "W8-BENE-24A_owner_documented_FFI",
      "W8-BENE-24B_FFI_owner_reporting",
      "W8-BENE-24C_auditor_letter",
      "W8-BENE-24D_contingent_beneficiaries",
      "W8-BENE-25A_restricted_distributors",
      "W8-BENE-25B_restricted_distributor_bound_agreement",
      "W8-BENE-25C_restricted_distributor_current_agreement",
      "W8-BENE-26_nonreporting_iga_country",
      "W8-BENE-26_model1_iga",
      "W8-BENE-26_model2_iga",
      "W8-BENE-26_trustee_us",
      "W8-BENE-26_trustee_foreign",
      "W8-BENE-27_foreign_government_checkbox",
      "W8-BENE-28A_international_org_7701_checkbox",
      "W8-BENE-28B_international_org_intergovernmental",
      "W8-BENE-29A_retirement_plan_treaty",
      "W8-BENE-29B_retirement_plan_organized",
      "W8-BENE-29C_retirement_plan_fewer_50",
      "W8-BENE-29D_retirement_plan_401a",
      "W8-BENE-29E_retirement_plan_earn_income",
      "W8-BENE-29F_retirement_plan_government_sponsored",
      "W8-BENE-30_entity_wholly_owned",
      "W8-BENE-31_territory_financial_institution",
      "W8-BENE-32_excepted_nonfinancial_group",
      "W8-BENE-34_excepted_liquidation",
      "W8-BENE_35_section_501c",
      "W8-BENE-37A_publicly_traded_nffe",
      "W8-BENE-38_excepted_territory_nffe",
      "W8-BENE-39_active_nffe",
      "W8-BENE-40A_traded_nffe",
      "W8-BENE-40B_passive_nffe_no_substantial_owners",
      "W8-BENE-40C_passive_nffe_substantial_owners_provided",
      "W8-BENE-41_affiliated_group",
      "W8-BENE-43_nffe",

      // Signing capacity
      "Signing_capacity",

      // Other legacy checkboxes
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    // Map all model fields to API payload
    Object.keys(model).forEach((field) => {
      const value = (model as any)[field];
      if (typeof value !== "function") {
        // Handle special field mappings (form field names to API field names)
        if (field === "14a") {
          // Map 14a form field to API field name
          if (value === true) {
            payload["W8-BENE-14A_beneficial_owner_resident"] = value;
          }
        } else if (field === "14b") {
          // Map 14b form field to API field name
          if (value === true) {
            payload["W8-BENE-14B_beneficial_owner_derives_item"] = value;
          }
        } else if (field === "14c") {
          // Map 14c form field to API field name
          if (value === true) {
            payload["W8-BENE-14C_claiming_treaty_benefits"] = value;
          }
        } else if (field === "BENE-15-Treaty_article_0") {
          // Map indexed treaty article field to API field name
          if (value && typeof value === "string" && value.trim() !== "") {
            payload["W8-BENE-15_treaty_provision_and_paragraph"] = value;
          }
        } else if (field === "BENE-15-Treaty_percentage_0") {
          // Map indexed treaty percentage field to API field name
          if (value && typeof value === "string" && value.trim() !== "") {
            payload["W8-BENE-15_withholding_rate_percentage"] = value;
          }
        } else if (field === "BENE-15-Treaty_typeOfIncome_0") {
          // Map indexed treaty income type field to API field name
          if (value && typeof value === "string" && value.trim() !== "") {
            payload["W8-BENE-15_income_type"] = value;
          }
        } else if (checkboxFields.includes(field)) {
          // 🔧 CRITICAL FIX: Always include checkbox values (both true AND false) in API payload
          // This ensures unchecked checkboxes are properly saved as false
          payload[field] = value === true ? true : false;
        } else {
          // For non-checkbox fields, include all defined values
          if (value !== undefined) {
            payload[field] = value;
          }
        }
      }
    });

    // 🛡️ CRITICAL: Always include isMapDone flag to preserve protection state
    if (model.isMapDone) {
      payload.isMapDone = true;
    }

    return payload;
  }

  static reset(model: W8BENEFormModel): void {
    Object.keys(model).forEach((key) => {
      if (typeof model[key] === "string") {
        model[key] = "";
      } else if (typeof model[key] === "boolean") {
        model[key] = false;
      }
    });
  }
}
